import useConnectWallet from "@/store/wallet/useConnectWallet";
import { useCallback, useState } from "react";

/**
 * TokenRewardConfig - ERC20 代币奖励配置
 */
export interface TokenRewardConfig {
    tokenAddress: string;   // ERC20 代币地址
    rewardAmount: bigint;   // 单次中奖获得的代币数量
    probability: bigint;    // 中奖概率 (1-100)
    enabled: boolean;       // 是否启用此代币抽奖
}

/**
 * TokenPoolInfo - 代币奖池信息
 */
export interface TokenPoolInfo {
    config: TokenRewardConfig;  // 代币奖励配置
    poolBalance: bigint;        // 当前奖池余额
}

/**
 * useGetTokenPoolInfo - 获取代币奖池信息 Hook
 * 
 * 用于调用 Lottery 合约的 getTokenPoolInfo 方法获取指定代币的奖池信息。
 * 
 * @returns {Object} 返回获取方法、结果和加载状态
 *   - getTokenPoolInfo: 执行获取奖池信息的函数
 *   - tokenPoolInfo: 代币奖池信息
 *   - tokenPoolInfoLod: 加载状态
 */
const useGetTokenPoolInfo = () => {
    // 存放数据
    const [tokenPoolInfo, setTokenPoolInfo] = useState<TokenPoolInfo | null>(null);
    // loading
    const [tokenPoolInfoLod, setLoading] = useState(false);
    // 获取合约实例
    const { contractEvm: { Lottery } } = useConnectWallet();

    /**
     * 获取指定代币的奖池信息
     * @param tokenAddress ERC20 代币地址
     * @returns 返回代币奖池信息
     */
    const getTokenPoolInfo = useCallback(async (tokenAddress: string) => {
        setLoading(true);
        if (Lottery) {
            try {
                // 调用合约的 getTokenPoolInfo 方法
                const result = await Lottery.getTokenPoolInfo(tokenAddress);
                const info: TokenPoolInfo = {
                    config: {
                        tokenAddress: result.config.tokenAddress,
                        rewardAmount: result.config.rewardAmount,
                        probability: result.config.probability,
                        enabled: result.config.enabled,
                    },
                    poolBalance: result.poolBalance
                };
                // 保存结果
                setTokenPoolInfo(info);
                setLoading(false);
                return info;
            } catch (e) {
                console.log("useGetTokenPoolInfo", e);
                setLoading(false);
                return null;
            }
        }
        setLoading(false);
        return null;
    }, [Lottery]);

    return { getTokenPoolInfo, tokenPoolInfo, tokenPoolInfoLod };
};

export default useGetTokenPoolInfo;
